/**
 * @file timestampFormatter.js
 * @description Formats message timestamps for chat interface
 *
 * Provides relative timestamps for recent messages (<24h) and absolute
 * timestamps for older messages (>24h), following ChatGPT/Claude.ai patterns.
 *
 * @example
 * formatTimestamp(new Date(Date.now() - 60000))        // "1 min ago"
 * formatTimestamp(new Date(Date.now() - 3600000))      // "1 hour ago"
 * formatTimestamp(new Date(Date.now() - 86400000))     // "Jan 15, 10:30 AM"
 */

/**
 * Formats timestamp for chat messages
 *
 * Rules:
 * - <1 minute: "just now"
 * - <60 minutes: "X min ago"
 * - <24 hours: "X hour(s) ago"
 * - ≥24 hours: Absolute format "Jan 15, 10:30 AM"
 *
 * @param {Date|string|number} timestamp - Timestamp to format
 * @returns {string} Formatted timestamp string
 */
export const formatTimestamp = (timestamp) => {
  try {
    const now = new Date();
    const messageDate = new Date(timestamp);

    // Validate date
    if (isNaN(messageDate.getTime())) {
      console.warn('Invalid timestamp provided to formatTimestamp:', timestamp);
      return '';
    }

    const diffMs = now - messageDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = diffMs / (1000 * 60 * 60);

    // Less than 1 minute
    if (diffMinutes < 1) {
      return 'just now';
    }

    // Less than 60 minutes
    if (diffMinutes < 60) {
      return `${diffMinutes} min ago`;
    }

    // Less than 24 hours
    if (diffHours < 24) {
      const hours = Math.floor(diffHours);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }

    // 24 hours or more: show absolute time
    const options = {
      month: 'short',     // "Jan", "Feb", etc.
      day: 'numeric',     // "1", "15", etc.
      hour: 'numeric',    // "1", "10", etc.
      minute: '2-digit',  // "00", "30", etc.
      hour12: true        // "AM"/"PM"
    };

    return messageDate.toLocaleString('en-US', options);
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return '';
  }
};

/**
 * Formats timestamp for tooltip (full date/time)
 * Used for showing detailed information on hover
 *
 * @param {Date|string|number} timestamp - Timestamp to format
 * @returns {string} Full formatted timestamp "January 15, 2025 at 10:30:45 AM"
 */
export const formatTimestampFull = (timestamp) => {
  try {
    const messageDate = new Date(timestamp);

    if (isNaN(messageDate.getTime())) {
      return '';
    }

    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };

    return messageDate.toLocaleString('en-US', options);
  } catch (error) {
    console.error('Error formatting full timestamp:', error);
    return '';
  }
};
